import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import { CheckCircle, TrendingUp, Award, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'

interface Question {
    id: number
    section: string
    subsection: string
    question: string
    options: string[]
    correctAnswer: number
}

export default function TestResultsPage() {
    const navigate = useNavigate()
    const location = useLocation()
    const { candidateName, totalQuestions, answered, questions, userAnswers } = location.state || {}

    // Convert array back to Map for easier lookup
    const userAnswersMap = new Map<number, number>(userAnswers || [])

    const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null)
    const [activeSubjectTab, setActiveSubjectTab] = useState<string>('all')

    if (!candidateName) {
        navigate('/student/dashboard')
        return null
    }

    // Mock score calculation (in real app, this would be calculated based on correct answers)
    // We can now calculate accurately if we want, but sticking to the passed/mock logic for consistency or refining it.
    // Let's refine it slightly to use the passed data if available.

    let correctCount = 0
    if (questions && userAnswersMap.size > 0) {
        questions.forEach((q: Question) => {
            if (userAnswersMap.get(q.id) === q.correctAnswer) {
                correctCount++
            }
        })
    } else {
        correctCount = Math.floor(answered * 0.75) // Fallback
    }

    const incorrectCount = answered - correctCount
    const score = (correctCount * 4) - (incorrectCount * 1)
    const maxScore = totalQuestions * 4
    const percentage = ((score / maxScore) * 100).toFixed(2)

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
            {/* Student Portal Header */}
            <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-4 sticky top-0 z-30">
                <div className="container mx-auto px-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-bold text-theme-primary">Test Results</h1>
                            <p className="text-sm text-theme-secondary">Detailed Performance Analysis</p>
                        </div>
                        <button
                            onClick={() => navigate('/student/dashboard')}
                            className="px-4 py-2 text-sm bg-primary-blue text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            </header>

            <div className="container mx-auto px-4 py-12">
                <div className="max-w-5xl mx-auto">
                    {/* Success Message */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center mb-12"
                    >
                        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="h-12 w-12 text-green-500" />
                        </div>
                        <h1 className="text-4xl font-display font-bold text-theme-primary mb-4">
                            Test Submitted Successfully!
                        </h1>
                        <p className="text-lg text-theme-secondary">
                            Thank you for completing the test, {candidateName}
                        </p>
                    </motion.div>

                    {/* Score Overview */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                        <GlassmorphicCard variant="elevated" className="p-8 text-center flex flex-col justify-center">
                            <h2 className="text-2xl font-display font-semibold text-theme-primary mb-6">
                                Your Score
                            </h2>
                            <div className="mb-6">
                                <div className="text-6xl font-bold text-primary-blue mb-2">
                                    {score}/{maxScore}
                                </div>
                                <div className="text-2xl text-theme-secondary">
                                    {percentage}%
                                </div>
                            </div>
                            <div className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-blue/10 border border-primary-blue/20 rounded-full self-center">
                                <Award className="h-5 w-5 text-primary-blue" />
                                <span className="text-primary-blue font-semibold">
                                    {parseFloat(percentage) >= 75 ? 'Excellent Performance!' : parseFloat(percentage) >= 50 ? 'Good Effort!' : 'Keep Practicing!'}
                                </span>
                            </div>
                        </GlassmorphicCard>

                        <GlassmorphicCard className="p-8">
                            <h2 className="text-xl font-semibold text-theme-primary mb-6">Performance stats</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
                                    <span className="block text-2xl font-bold text-green-600 dark:text-green-400">{correctCount}</span>
                                    <span className="text-sm text-theme-secondary">Correct</span>
                                </div>
                                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                                    <span className="block text-2xl font-bold text-red-600 dark:text-red-400">{incorrectCount}</span>
                                    <span className="text-sm text-theme-secondary">Incorrect</span>
                                </div>
                                <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
                                    <span className="block text-2xl font-bold text-theme-primary">{answered}</span>
                                    <span className="text-sm text-theme-secondary">Attempted</span>
                                </div>
                                <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
                                    <span className="block text-2xl font-bold text-theme-primary">{totalQuestions}</span>
                                    <span className="text-sm text-theme-secondary">Total Qstns</span>
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </div>

                    {/* Detailed Question Analysis - Subject-wise */}
                    {questions && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <GlassmorphicCard className="p-6 md:p-8 mb-8">
                                <h2 className="text-2xl font-display font-semibold text-theme-primary mb-6 flex items-center gap-2">
                                    <TrendingUp className="h-6 w-6 text-primary-blue" />
                                    Question-wise Analysis
                                </h2>

                                {/* Subject Tabs */}
                                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                                    {['all', 'Physics', 'Chemistry', 'Biology'].map((subject) => (
                                        <button
                                            key={subject}
                                            onClick={() => setActiveSubjectTab(subject)}
                                            className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${activeSubjectTab === subject
                                                ? 'bg-primary-blue text-white'
                                                : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-slate-200 dark:hover:bg-slate-700'
                                                }`}
                                        >
                                            {subject === 'all' ? 'All Questions' : subject}
                                        </button>
                                    ))}
                                </div>

                                <div className="space-y-4">
                                    {questions
                                        .filter((q: Question) => activeSubjectTab === 'all' || q.section === activeSubjectTab)
                                        .map((q: Question, i: number) => {
                                            const userAnswer = userAnswersMap.get(q.id)
                                            const isCorrect = userAnswer === q.correctAnswer
                                            const isSkipped = userAnswer === undefined

                                            // Only show first 50 for performance
                                            if (i >= 50) return null

                                            return (
                                                <div key={q.id} className="border border-slate-200 dark:border-slate-700/50 rounded-xl overflow-hidden bg-white dark:bg-slate-900/40">
                                                    <button
                                                        onClick={() => setExpandedQuestion(expandedQuestion === q.id ? null : q.id)}
                                                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-4 text-left">
                                                            <div className={`
                                                            w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-white
                                                            ${isSkipped ? 'bg-slate-400' : isCorrect ? 'bg-green-500' : 'bg-red-500'}
                                                        `}>
                                                                {i + 1}
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-theme-primary line-clamp-1">{q.question}</p>
                                                                <p className="text-xs text-theme-secondary mt-1">
                                                                    {isSkipped ? 'Skipped' : isCorrect ? 'Correct' : 'Incorrect'} • {q.section}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {expandedQuestion === q.id ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                                                    </button>

                                                    <AnimatePresence>
                                                        {expandedQuestion === q.id && (
                                                            <motion.div
                                                                initial={{ height: 0 }}
                                                                animate={{ height: 'auto' }}
                                                                exit={{ height: 0 }}
                                                                className="overflow-hidden bg-slate-50 dark:bg-slate-900/30"
                                                            >
                                                                <div className="p-4 border-t border-slate-200 dark:border-slate-700/50">
                                                                    <p className="text-lg text-theme-primary mb-4 p-4 bg-white dark:bg-slate-800 rounded-lg">{q.question}</p>

                                                                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                                                                        {q.options.map((opt, idx) => (
                                                                            <div key={idx} className={`
                                                                            p-3 rounded-lg border-2 flex items-center gap-3
                                                                            ${idx === q.correctAnswer
                                                                                    ? 'border-green-500 bg-green-500/10'
                                                                                    : idx === userAnswer
                                                                                        ? 'border-red-500 bg-red-500/10'
                                                                                        : 'border-slate-200 dark:border-slate-700'
                                                                                }
                                                                        `}>
                                                                                <div className={`
                                                                                w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                                                                                ${idx === q.correctAnswer
                                                                                        ? 'bg-green-500 text-white'
                                                                                        : idx === userAnswer
                                                                                            ? 'bg-red-500 text-white'
                                                                                            : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                                                                    }
                                                                            `}>
                                                                                    {String.fromCharCode(65 + idx)}
                                                                                </div>
                                                                                <span className={`text-sm ${idx === q.correctAnswer ? 'font-bold text-green-700 dark:text-green-400' : 'text-theme-secondary'
                                                                                    }`}>{opt}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>

                                                                    <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/20 rounded-lg">
                                                                        <p className="font-bold text-primary-blue mb-1">Explanation</p>
                                                                        <p className="text-sm text-theme-secondary">
                                                                            Detailed explanation for Question {i + 1} would appear here. It explains why Option {String.fromCharCode(65 + q.correctAnswer)} is correct and why other options are incorrect.
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            )
                                        })}
                                    {questions.length > 50 && (
                                        <p className="text-center text-sm text-theme-secondary italic mt-4">Showing first 50 questions only.</p>
                                    )}
                                </div>
                            </GlassmorphicCard>
                        </motion.div>
                    )}

                    {/* Action Buttons */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="flex gap-4 justify-center"
                    >
                        <button
                            onClick={() => navigate('/student/dashboard')}
                            className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg transition-colors"
                        >
                            Back to Dashboard
                        </button>
                        <button
                            onClick={() => navigate('/student/dashboard')}
                            className="px-8 py-4 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                        >
                            Return to Dashboard
                            <ArrowRight className="h-5 w-5" />
                        </button>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}
